import { Component, ChangeDetectionStrategy, computed, input, model } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { EstatusVacaciones, SolicitudVacaciones } from '@schemas/vacaciones.schema';
import { getEstatusTono, getPasosFlujo, type PasoFlujo } from '@shared/vacaciones.helpers';

import { DetailField } from '../detail-field/detail-field';

import { PageHeader, SectionHeader, StatusText } from '@intelishs-group/ui';

/** Cómo se pinta cada etapa del seguimiento. */

@Component({
  selector: 'app-solicitud-resolucion-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DetailField, DatePipe, NzModalModule, PageHeader, SectionHeader, StatusText],
  templateUrl: './solicitud-resolucion-modal.html',
  styleUrl: './solicitud-resolucion-modal.css',
})
export class SolicitudResolucionModal {
  visible = model(false);
  solicitud = input<SolicitudVacaciones | null>(null);

  /** Tono del punto de estado, para `<intelishs-status-text [tone]>`. */
  tono = computed(() => {
    const sol = this.solicitud();
    return sol ? getEstatusTono(sol.estatus) : 'neutro';
  });

  /**
   * El estatus en lenguaje del empleado. Los valores de la máquina de estados
   * ("Aprobada Director") describen el paso del flujo, no lo que le pasó a su
   * solicitud: leído solo, no dice si ya puede tomar los días o falta un paso.
   *
   * Los pendientes se nombran igual que la etapa del seguimiento en la que
   * están paradas, así el subtítulo y la lista de abajo dicen lo mismo. Textos
   * cortos a propósito: el subtítulo de `page-header` va en mayúsculas con
   * tracking, y una frase larga ahí se vuelve ilegible.
   */
  estadoTexto = computed(() => {
    const sol = this.solicitud();
    if (!sol) return '';

    const textos: Record<EstatusVacaciones, string> = {
      Pendiente: 'Revisión del director',
      'Aprobada Director': 'Validación de RH',
      Aprobada: 'Aprobada',
      'Rechazada Director': 'Rechazada por tu director',
      Rechazada: 'Rechazada por RH',
      Cancelada: 'Cancelada',
    };

    return textos[sol.estatus] ?? sol.estatus;
  });

  /**
   * Las tres etapas del flujo, no solo la actual. Un bloque suelto con el
   * estatus le dice al empleado dónde está pero no cuánto falta; el recorrido
   * completo responde las dos cosas de una vez, y resuelve sin ramas el caso
   * "Aprobada Director", que antes se dibujaba con dos avisos apilados.
   *
   * Vista del empleado: deliberadamente sin nombres ni comentarios de Director
   * o RH — esos quedan en los modales de aprobación.
   */
  /**
   * Los pasos salen del helper compartido con el modal de detalle: una sola
   * máquina de estados para el mismo proceso. Este modal pinta `titulo`,
   * `fecha` y `detalle`, y NUNCA `nombre` ni `comentario` —lo que dijeron el
   * director y RH queda reservado a los modales de RH/admin—.
   */
  pasos = computed((): PasoFlujo[] => {
    const sol = this.solicitud();
    return sol ? getPasosFlujo(sol) : [];
  });

  cerrar(): void {
    this.visible.set(false);
  }
}
